import React from "react";

import styles from "./SignHistoryV2.module.css";

const EyeIcon = () => (
  <svg viewBox="0 0 640 640" width="16" height="16" aria-hidden="true">
    <path fill="currentColor" d="M320 96C239.2 96 174.5 132.8 127.4 176.6C80.6 220.1 49.3 272 34.4 307.7C31.1 315.6 31.1 324.4 34.4 332.3C49.3 368 80.6 420 127.4 463.4C174.5 507.1 239.2 544 320 544C400.8 544 465.5 507.2 512.6 463.4C559.4 419.9 590.7 368 605.6 332.3C608.9 324.4 608.9 315.6 605.6 307.7C590.7 272 559.4 220 512.6 176.6C465.5 132.9 400.8 96 320 96zM176 320C176 240.5 240.5 176 320 176C399.5 176 464 240.5 464 320C464 399.5 399.5 464 320 464C240.5 464 176 399.5 176 320zM320 256C320 291.3 291.3 320 256 320C244.5 320 233.7 317 224.3 311.6C223.3 322.5 224.2 333.7 227.2 344.8C240.9 396 293.6 426.4 344.8 412.7C396 399 426.4 346.3 412.7 295.1C400.5 249.4 357.2 220.3 311.6 224.3C316.9 233.6 320 244.4 320 256z" />
  </svg>
);

const MinusIcon = () => (
  <svg viewBox="0 0 640 640" width="16" height="16" aria-hidden="true">
    <path fill="currentColor" d="M96 320C96 302.3 110.3 288 128 288L512 288C529.7 288 544 302.3 544 320C544 337.7 529.7 352 512 352L128 352C110.3 352 96 337.7 96 320z" />
  </svg>
);

const getStaffStatus = (staff) => {
  if (staff && staff.has_declined) {
    return "declined";
  }

  if (staff && staff.staff_sign) {
    return "signed";
  }

  return "not-signed";
};

const staffStatusMeta = {
  signed: {
    className: styles.crewSigned,
    title: "Signed"
  },
  declined: {
    className: styles.crewDeclined,
    title: "Declined"
  },
  "not-signed": {
    className: styles.crewNotSigned,
    title: "Not Signed"
  }
};

function SignHistoryV2CrewList({ shownStaffs, sign }) {
  if (!shownStaffs || shownStaffs.length === 0) {
    return <span className={styles.emptyText}>No staff</span>;
  }

  return shownStaffs.map((staff) => {
    const status = getStaffStatus(staff);
    const statusMeta = staffStatusMeta[status] || staffStatusMeta["not-signed"];

    return (
      <span
        key={`${sign.task_id}_${sign.swms_id}_${sign.shift_id}_${staff.user_name}`}
        className={`${styles.crewName} ${statusMeta.className}`}
        title={statusMeta.title}
      >
        {staff.staff_name}
      </span>
    );
  });
}

export default function SignHistoryV2Row({ sign, selectedStaff, jobId, onOpenPreStart }) {
  const rowStaffs = sign && Array.isArray(sign.staffs) ? sign.staffs : [];
  const shownStaffs = selectedStaff
    ? rowStaffs.filter((staff) => staff.user_name === selectedStaff)
    : rowStaffs;

  const signedCount = rowStaffs.filter((staff) => staff.staff_sign && !staff.has_declined).length;
  const expectedCount = rowStaffs.filter((staff) => !staff.has_declined).length;
  const isFullySigned = expectedCount > 0 && signedCount === expectedCount;

  const swmsAvailable = sign && sign.pdfStatus === "generated" && Boolean(sign.pdf_link);
  const preStartAvailableFromApi = Boolean(sign && sign.pre_start_available);
  const preStartAvailable = Boolean(sign && sign.shift_id && sign.swms_id && preStartAvailableFromApi);

  return (
    <tr>
      <td className={styles.dateCell}>{sign.shift_date || ""}</td>
      <td className={styles.shiftCell}>{sign.shift_id || ""}</td>
      <td className={styles.taskCell} title={Array.isArray(sign.task_name) ? sign.task_name.join(", ") : sign.task_name}>
        {Array.isArray(sign.task_name) ? sign.task_name.join(", ") : sign.task_name}
      </td>
      <td className={styles.swmsIdCell} title={sign.swms_number || ""}>{sign.swms_number || ""}</td>

      <td className={styles.docsCell}>
        <div className={styles.docGroup}>
          <div className={styles.docAction}>
            <button
              type="button"
              className={`${styles.docButton} ${!swmsAvailable ? styles.docButtonDisabled : ""}`}
              title={swmsAvailable ? "View SWMS" : "SWMS document not available"}
              aria-label={swmsAvailable ? "View SWMS" : "SWMS document not available"}
              disabled={!swmsAvailable}
              onClick={() => {
                if (swmsAvailable) {
                  window.open(sign.pdf_link, "_blank", "noopener,noreferrer");
                }
              }}
            >
              {swmsAvailable ? <EyeIcon /> : <MinusIcon />}
            </button>
            <span className={`${styles.docLabel} ${swmsAvailable ? styles.docLabelSwmsAvailable : ""}`}>SWMS</span>
          </div>

          <div className={styles.docAction}>
            <button
              type="button"
              className={`${styles.docButton} ${!preStartAvailable ? styles.docButtonDisabled : ""}`}
              title={preStartAvailable ? "View Pre-Start" : "Pre-Start document is not available"}
              aria-label={preStartAvailable ? "View Pre-Start" : "Pre-Start document is not available"}
              disabled={!preStartAvailable}
              onClick={() => {
                if (!preStartAvailable || typeof onOpenPreStart !== "function") {
                  return;
                }

                onOpenPreStart({
                  jobId: sign.job_id || jobId,
                  shiftId: sign.shift_id,
                  swmsId: sign.swms_id
                });
              }}
            >
              {preStartAvailable ? <EyeIcon /> : <MinusIcon />}
            </button>
            <span className={`${styles.docLabel} ${preStartAvailable ? styles.docLabelPreStartAvailable : ""}`}>Pre-Start</span>
          </div>
        </div>
      </td>

      <td>
        <span className={`${styles.signRatio} ${isFullySigned ? styles.signRatioFull : styles.signRatioPartial}`}>
          {signedCount}/{expectedCount}
        </span>
      </td>

      <td className={styles.crewCell}>
        <div className={styles.crewList}>
          <SignHistoryV2CrewList shownStaffs={shownStaffs} sign={sign} />
        </div>
      </td>
    </tr>
  );
}
